import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    output,
    inject,
    Injector,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportChecklistUnits,
    IChecklistV2ReportParameterFilter,
} from '../../../../../shared/fi-checklist2-report';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { MatSortDirection, ObjectTypes } from '@fi-shared/types';
import { FiChecklist2ReportHelperService } from '../../../../../services/fi-checklist2-report-helper.service';
import { Sort } from '@angular/material/sort';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiChecklist2ReportDialogsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-dialogs.service';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';

@Component({
    selector: 'fi-checklist-details-units-table',
    templateUrl: './checklist-details-units-table.component.html',
    styleUrls: ['./checklist-details-units-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-details-units-table' },
    standalone: false,
})
export class FiChecklistDetailsUnitsTableComponent
    extends FiInfiniteScrollDataSource<IChecklist2ReportChecklistUnits>
    implements OnChanges
{
    @Input() checklistUnits: IChecklist2ReportChecklistUnits[];
    @Input() checklistSummary: IChecklist2ReportChecklistSummary;
    @Input() isAuditMode: boolean;
    @Input() filter: IChecklistV2ReportParameterFilter;
    @Input() set isUnitsInitialLoadFinished(value: boolean) {
        if (value) {
            this.initSelectedSort();
            this.onSortChanged(this.selectedSort);
        }
    }

    public sortChange = output<void>();

    protected getRowClassFn = (): string => 'cursor-pointer';

    public readonly defaultSortColumnName: string = 'NotOkCheckpointsCount';
    public readonly defaultSortDirection = true;
    public readonly itemsListType = FiItemsListType.ChecklistReportDetailsUnitsTable;
    public readonly objectType: ObjectTypes = ObjectTypes.Checklist2;

    private readonly businessUnitNameColumnName: string = 'BusinessUnitName';
    private fiChecklist2ReportHelperService = inject(FiChecklist2ReportHelperService);
    private fiChecklist2ReportDialogsService = inject(FiChecklist2ReportDialogsService);

    constructor(injector: Injector) {
        super(injector);
    }

    protected initSelectedSort() {
        super.initSelectedSort();

        const haveItemsDeviations: boolean = this.checklistUnits.some((item: IChecklist2ReportChecklistUnits) => {
            return !!this.getDeviationsCount(item);
        });

        if (haveItemsDeviations) {
            this.selectedSort.active = this.defaultSortColumnName;
            this.selectedSort.direction = MatSortDirection.Descending;
        } else {
            this.selectedSort.active = this.businessUnitNameColumnName;
            this.selectedSort.direction = MatSortDirection.Ascending;
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.checklistUnits;
        if (!!change) {
            this.reloadData().finally(() => this.cdr.detectChanges());
        }
    }

    public getListData(): Promise<IChecklist2ReportChecklistUnits[]> {
        return Promise.resolve(this.checklistUnits);
    }

    protected tableSortChange($event: Sort): void {
        this.onSortChanged($event);
        this.sortChange.emit();
    }

    protected getDeviationsCount(checklistUnits: IChecklist2ReportChecklistUnits): number {
        return checklistUnits.NotOkCheckpointsCount;
    }

    protected showUnitsDetailsOverlay(checklistUnits: IChecklist2ReportChecklistUnits): void {
        this.fiChecklist2ReportDialogsService.showChecklistUnitAnswerDetailsDialog({
            checklistSummary: this.checklistSummary,
            checklistUnits,
            auditMode: this.isAuditMode,
            reportType: FiChecklist2ReportType.OpenChecklists,
            filter: Object.assign(this.filter, { BusinessUnitIds: [checklistUnits.BusinessUnitId] }),
        });
    }

    protected getFilledCheckpointsCount(checklistUnits: IChecklist2ReportChecklistUnits): number {
        return this.fiChecklist2ReportHelperService.getChecklistUnitsFilledCheckpoints(checklistUnits);
    }

    protected getTotalCheckpointsCount(checklistUnits: IChecklist2ReportChecklistUnits): number {
        return this.fiChecklist2ReportHelperService.getChecklistUnitsTotalCheckpoints(checklistUnits);
    }
}
